/**
 * SupportTicketsPage — /support/tickets
 *
 * Ticket submission form (no live chat). Submitting creates a contact +
 * conversation + message via Chatwoot's public Client API, which shows up
 * as a ticket in the Chatwoot inbox — but the user never sees a live chat
 * window. Website token comes from the white-label runtime config — see
 * partners/<partner>/app-config.js.
 */

import React, { useState } from "react";

const CHATWOOT_BASE_URL = "https://app.chatwoot.com";

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 6,
  border: "1px solid #ddd",
  fontSize: 14,
  marginTop: 4,
  boxSizing: "border-box",
};

export default function SupportTicketsPage() {
  const websiteToken = window.APP_CONFIG?.chatwootWebsiteToken;

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | sent | error

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      const contactRes = await fetch(
        `${CHATWOOT_BASE_URL}/public/api/v1/inboxes/${websiteToken}/contacts`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: form.name, email: form.email }),
        }
      );
      const contact = await contactRes.json();
      const contactIdentifier = contact.source_id;

      const conversationRes = await fetch(
        `${CHATWOOT_BASE_URL}/public/api/v1/inboxes/${websiteToken}/contacts/${contactIdentifier}/conversations`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      const conversation = await conversationRes.json();

      await fetch(
        `${CHATWOOT_BASE_URL}/public/api/v1/inboxes/${websiteToken}/contacts/${contactIdentifier}/conversations/${conversation.id}/messages`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: `Subject: ${form.subject}\n\n${form.message}` }),
        }
      );

      setStatus("sent");
    } catch (err) {
      setStatus("error");
    }
  };

  if (!websiteToken) {
    return (
      <div style={{
        height: "calc(100vh - 60px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#aaa", fontSize: 13, textAlign: "center", padding: 24,
      }}>
        Support tickets aren't configured yet.<br />
        Set <code>chatwootWebsiteToken</code> in the partner app-config.js.
      </div>
    );
  }

  if (status === "sent") {
    return (
      <div style={{
        height: "calc(100vh - 60px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", padding: 24,
      }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
        <div style={{ fontWeight: 600, fontSize: 16, color: "#333" }}>Ticket submitted</div>
        <div style={{ fontSize: 13, color: "#888", marginTop: 4, textAlign: "center" }}>
          Our support team will get back to you by email.
        </div>
        <button
          onClick={() => { setForm({ name: "", email: "", subject: "", message: "" }); setStatus("idle"); }}
          style={{ marginTop: 16, padding: "8px 16px", borderRadius: 6, border: "1px solid #ddd", background: "#fff", cursor: "pointer" }}
        >
          Submit another ticket
        </button>
      </div>
    );
  }

  return (
    <div style={{ height: "calc(100vh - 60px)", background: "#f7f8fc", overflowY: "auto" }}>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "32px 24px" }}>
        <div style={{ fontWeight: 700, fontSize: 18, color: "#333", marginBottom: 4 }}>🎫 Submit a Support Ticket</div>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 24 }}>
          Describe your issue and we'll follow up by email.
        </div>

        <form onSubmit={handleSubmit} style={{ background: "#fff", borderRadius: 8, padding: 24, border: "1px solid #eee" }}>
          <label style={{ display: "block", fontSize: 13, color: "#555", marginBottom: 12 }}>
            Name
            <input type="text" required value={form.name} onChange={handleChange("name")} style={inputStyle} />
          </label>
          <label style={{ display: "block", fontSize: 13, color: "#555", marginBottom: 12 }}>
            Email
            <input type="email" required value={form.email} onChange={handleChange("email")} style={inputStyle} />
          </label>
          <label style={{ display: "block", fontSize: 13, color: "#555", marginBottom: 12 }}>
            Subject
            <input type="text" required value={form.subject} onChange={handleChange("subject")} style={inputStyle} />
          </label>
          <label style={{ display: "block", fontSize: 13, color: "#555", marginBottom: 16 }}>
            Message
            <textarea required rows={6} value={form.message} onChange={handleChange("message")} style={{ ...inputStyle, resize: "vertical" }} />
          </label>

          {status === "error" && (
            <div style={{ color: "#c0392b", fontSize: 13, marginBottom: 12 }}>
              Something went wrong submitting your ticket. Please try again.
            </div>
          )}

          <button
            type="submit"
            disabled={status === "submitting"}
            style={{
              width: "100%", padding: "10px 0", borderRadius: 6, border: "none",
              background: "#545cd8", color: "#fff", fontWeight: 600, fontSize: 14,
              cursor: status === "submitting" ? "default" : "pointer",
              opacity: status === "submitting" ? 0.7 : 1,
            }}
          >
            {status === "submitting" ? "Submitting…" : "Submit Ticket"}
          </button>
        </form>
      </div>
    </div>
  );
}
